import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  createPBCRequest,
  fetchPBCControlOptions,
  fetchPBCRequests,
  updatePBCRequest,
  updatePBCStatus,
  type PBCControlOption,
  type PBCCreateBody,
  type PBCListParams,
  type PBCRequest,
  type PBCStatus,
  type PBCUpdateBody,
} from '../../request/pbc/pbcRequests.ts';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { pbcQueryKeys } from '../../queries/pbc/keys.ts';

/**
 * React Query v5 hooks for the PBC (Provided-by-Client) request tracker
 * (Feature 5.3, Controller report #29). Writes are gated server-side by
 * `RequireOrgAdminForWrites()` — non-admins get a 403 on create/update,
 * which surfaces via `error` on the mutation result for the caller to
 * detect and disable write controls (see PBCTracker.tsx).
 */

export function usePBCRequests(params: PBCListParams = {}): UseQueryResult<PBCRequest[], Error> {
  return useQuery({
    queryKey: pbcQueryKeys.list(params),
    queryFn: () => fetchPBCRequests(params),
  });
}

export function usePBCControlOptions(): UseQueryResult<PBCControlOption[], Error> {
  return useQuery({
    queryKey: pbcQueryKeys.controlOptions(),
    queryFn: fetchPBCControlOptions,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreatePBCRequest(): UseMutationResult<PBCRequest, Error, PBCCreateBody> {
  const queryClient = useQueryClient();
  return useMutation<PBCRequest, Error, PBCCreateBody>({
    mutationFn: createPBCRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pbcQueryKeys.all });
      toast.success('PBC request created');
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to create PBC request')));
    },
  });
}

export function useUpdatePBCStatus(): UseMutationResult<PBCRequest, Error, { id: string; status: PBCStatus }> {
  const queryClient = useQueryClient();
  return useMutation<PBCRequest, Error, { id: string; status: PBCStatus }>({
    mutationFn: ({ id, status }) => updatePBCStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pbcQueryKeys.all });
      toast.success('PBC request status updated');
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to update PBC request status')));
    },
  });
}

export function useUpdatePBCRequest(): UseMutationResult<PBCRequest, Error, { id: string; body: PBCUpdateBody }> {
  const queryClient = useQueryClient();
  return useMutation<PBCRequest, Error, { id: string; body: PBCUpdateBody }>({
    mutationFn: ({ id, body }) => updatePBCRequest(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pbcQueryKeys.all });
      toast.success('PBC request updated');
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to update PBC request')));
    },
  });
}
